import { supabase } from "@/integrations/supabase/client";
import { EmailCampaign, EmailCampaignRecipient } from "@/types/communication.types";

export interface CampaignMetrics {
  id: string;
  name: string;
  totalRecipients: number;
  deliveredCount: number;
  deliveryRate: number;
  openCount: number;
  openRate: number;
  clickCount: number;
  clickRate: number;
  responseCount: number;
  responseRate: number;
  bounceCount: number;
  bounceRate: number;
  unsubscribeCount: number;
  unsubscribeRate: number;
  period?: string;
  templateId?: string;
  templateName?: string;
}

export interface CampaignAnalytics {
  campaigns: CampaignMetrics[];
  summary: {
    totalCampaigns: number;
    totalRecipients: number;
    averageDeliveryRate: number;
    averageOpenRate: number;
    averageClickRate: number;
    averageResponseRate: number;
    campaignsOverTime: PeriodMetric[];
  };
  topPerforming: CampaignMetrics[];
  campaignTypes: {
    name: string;
    count: number;
    openRate: number;
    clickRate: number;
  }[];
}

export interface ContactEngagement {
  contactId: string;
  contactName: string;
  organizationId?: string;
  organizationName?: string;
  totalCampaigns: number;
  openRate: number;
  clickRate: number;
  responseRate: number;
  lastEngagement?: string;
  recentActivity: {
    campaignId: string;
    campaignName: string;
    date: string;
    action: string;
  }[];
}

export interface PeriodMetric {
  period: string;
  campaigns: number;
  recipients: number;
  openRate: number;
  clickRate: number;
  responseRate: number;
}

export interface CampaignAnalyticsParams {
  period?: 'week' | 'month' | 'quarter' | 'year';
  startDate?: string;
  endDate?: string;
  campaignIds?: string[];
  organizationId?: string;
}

/**
 * Get analytics for all email campaigns
 */
export const getCampaignAnalytics = async (
  params?: CampaignAnalyticsParams
): Promise<CampaignAnalytics> => {
  try {
    // Set up date filters based on params
    const endDate = params?.endDate ? new Date(params.endDate) : new Date();
    let startDate = new Date();
    
    if (params?.startDate) {
      startDate = new Date(params.startDate);
    } else if (params?.period) {
      // Calculate start date based on period
      switch (params.period) {
        case 'week':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(startDate.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(startDate.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
      }
    } else {
      // Default to last 6 months
      startDate.setMonth(startDate.getMonth() - 6);
    }
    
    // Format dates for query
    const startDateStr = startDate.toISOString();
    const endDateStr = endDate.toISOString();
    
    // Fetch campaigns from database
    let campaignQuery = supabase
      .from('email_campaigns')
      .select(`
        id,
        name,
        created_at,
        campaign_type,
        template_id,
        templates(name),
        email_campaign_stats(
          total_recipients,
          delivered_count,
          open_count,
          click_count,
          response_count,
          bounce_count,
          unsubscribe_count
        )
      `)
      .gte('created_at', startDateStr)
      .lte('created_at', endDateStr);
      
    // Apply additional filters
    if (params?.campaignIds && params.campaignIds.length > 0) {
      campaignQuery = campaignQuery.in('id', params.campaignIds);
    }
    
    if (params?.organizationId) {
      campaignQuery = campaignQuery.eq('organization_id', params.organizationId);
    }
    
    // Get campaign data
    const { data: campaignData, error: campaignError } = await campaignQuery;
    
    if (campaignError) {
      throw campaignError;
    }
    
    // Transform data to required format
    const campaigns: CampaignMetrics[] = campaignData?.map(campaign => {
      // Extract stats from joined table
      const stats = campaign.email_campaign_stats?.[0] || {
        total_recipients: 0,
        delivered_count: 0,
        open_count: 0,
        click_count: 0,
        response_count: 0,
        bounce_count: 0,
        unsubscribe_count: 0
      };
      
      // Handle divide by zero cases safely
      const totalRecipients = stats.total_recipients || 0;
      const deliveredCount = stats.delivered_count || 0;
      const openCount = stats.open_count || 0;
      const clickCount = stats.click_count || 0;
      const responseCount = stats.response_count || 0;
      const bounceCount = stats.bounce_count || 0;
      const unsubscribeCount = stats.unsubscribe_count || 0;
      
      // Calculate rates
      const deliveryRate = totalRecipients > 0 ? (deliveredCount / totalRecipients) * 100 : 0;
      const openRate = deliveredCount > 0 ? (openCount / deliveredCount) * 100 : 0;
      const clickRate = deliveredCount > 0 ? (clickCount / deliveredCount) * 100 : 0;
      const responseRate = deliveredCount > 0 ? (responseCount / deliveredCount) * 100 : 0;
      const bounceRate = totalRecipients > 0 ? (bounceCount / totalRecipients) * 100 : 0;
      const unsubscribeRate = deliveredCount > 0 ? (unsubscribeCount / deliveredCount) * 100 : 0;
      
      // Format period (YYYY-MM)
      const date = new Date(campaign.created_at);
      const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      return {
        id: campaign.id,
        name: campaign.name,
        totalRecipients,
        deliveredCount,
        deliveryRate,
        openCount,
        openRate,
        clickCount,
        clickRate,
        responseCount,
        responseRate,
        bounceCount,
        bounceRate,
        unsubscribeCount,
        unsubscribeRate,
        period,
        templateId: campaign.template_id,
        templateName: campaign.templates?.name || 'Unknown'
      };
    }) || [];
    
    // If no campaigns found, return empty data
    if (campaigns.length === 0) {
      return {
        campaigns: [],
        summary: {
          totalCampaigns: 0,
          totalRecipients: 0,
          averageDeliveryRate: 0,
          averageOpenRate: 0,
          averageClickRate: 0,
          averageResponseRate: 0,
          campaignsOverTime: []
        },
        topPerforming: [],
        campaignTypes: []
      };
    }
    
    // Calculate summary metrics
    const totalCampaigns = campaigns.length;
    const totalRecipients = campaigns.reduce((sum, c) => sum + c.totalRecipients, 0);
    const averageDeliveryRate = campaigns.reduce((sum, c) => sum + c.deliveryRate, 0) / totalCampaigns;
    const averageOpenRate = campaigns.reduce((sum, c) => sum + c.openRate, 0) / totalCampaigns;
    const averageClickRate = campaigns.reduce((sum, c) => sum + c.clickRate, 0) / totalCampaigns;
    const averageResponseRate = campaigns.reduce((sum, c) => sum + c.responseRate, 0) / totalCampaigns;
    
    // Create period metrics (group by period)
    const periodMap = new Map<string, {
      campaigns: number,
      recipients: number,
      opens: number,
      clicks: number,
      responses: number
    }>();
    
    campaigns.forEach(campaign => {
      const period = campaign.period || '';
      const current = periodMap.get(period) || {
        campaigns: 0,
        recipients: 0,
        opens: 0,
        clicks: 0,
        responses: 0
      };
      
      current.campaigns += 1;
      current.recipients += campaign.totalRecipients;
      current.opens += campaign.openCount;
      current.clicks += campaign.clickCount;
      current.responses += campaign.responseCount;
      
      periodMap.set(period, current);
    });
    
    const campaignsOverTime: PeriodMetric[] = Array.from(periodMap.entries())
      .map(([period, data]) => ({
        period,
        campaigns: data.campaigns,
        recipients: data.recipients,
        openRate: data.recipients > 0 ? (data.opens / data.recipients) * 100 : 0,
        clickRate: data.recipients > 0 ? (data.clicks / data.recipients) * 100 : 0,
        responseRate: data.recipients > 0 ? (data.responses / data.recipients) * 100 : 0
      }))
      .sort((a, b) => a.period.localeCompare(b.period));
    
    // Group campaigns by type
    const campaignTypeMap = new Map<string, {
      count: number,
      opens: number,
      clicks: number,
      delivered: number
    }>();
    
    campaigns.forEach(campaign => {
      const campaignData = campaignData?.find(c => c.id === campaign.id);
      const type = campaignData?.campaign_type || 'Other';
      
      const current = campaignTypeMap.get(type) || {
        count: 0,
        opens: 0,
        clicks: 0,
        delivered: 0
      };
      
      current.count += 1;
      current.opens += campaign.openCount;
      current.clicks += campaign.clickCount;
      current.delivered += campaign.deliveredCount;
      
      campaignTypeMap.set(type, current);
    });
    
    const campaignTypes = Array.from(campaignTypeMap.entries())
      .map(([name, data]) => ({
        name,
        count: data.count,
        openRate: data.delivered > 0 ? (data.opens / data.delivered) * 100 : 0,
        clickRate: data.delivered > 0 ? (data.clicks / data.delivered) * 100 : 0
      }));
    
    // Find top performing campaigns by engagement (open rate + click rate)
    const topPerforming = [...campaigns]
      .sort((a, b) => (b.openRate + b.clickRate) - (a.openRate + a.clickRate))
      .slice(0, 3);
    
    return {
      campaigns,
      summary: {
        totalCampaigns,
        totalRecipients,
        averageDeliveryRate,
        averageOpenRate,
        averageClickRate,
        averageResponseRate,
        campaignsOverTime
      },
      topPerforming,
      campaignTypes
    };
  } catch (error) {
    // Return empty data structure on error
    return {
      campaigns: [],
      summary: {
        totalCampaigns: 0,
        totalRecipients: 0,
        averageDeliveryRate: 0,
        averageOpenRate: 0,
        averageClickRate: 0,
        averageResponseRate: 0,
        campaignsOverTime: []
      },
      topPerforming: [],
      campaignTypes: []
    };
  }
};

/**
 * Get metrics for a specific campaign
 */
export const getCampaignMetrics = async (
  campaignId: string
): Promise<CampaignMetrics> => {
  try {
    // Fetch campaign data directly from the database
    const { data: campaign, error } = await supabase
      .from('email_campaigns')
      .select(`
        id,
        name,
        created_at,
        campaign_type,
        template_id,
        templates(name),
        email_campaign_stats(
          total_recipients,
          delivered_count,
          open_count,
          click_count,
          response_count,
          bounce_count,
          unsubscribe_count
        )
      `)
      .eq('id', campaignId)
      .single();
    
    if (error) {
      throw error;
    }
    
    if (!campaign) {
      throw new Error(`Campaign with ID ${campaignId} not found`);
    }
    
    // Extract stats from joined table
    const stats = campaign.email_campaign_stats?.[0] || {
      total_recipients: 0,
      delivered_count: 0,
      open_count: 0,
      click_count: 0,
      response_count: 0,
      bounce_count: 0,
      unsubscribe_count: 0
    };
    
    // Handle divide by zero cases safely
    const totalRecipients = stats.total_recipients || 0;
    const deliveredCount = stats.delivered_count || 0;
    const openCount = stats.open_count || 0;
    const clickCount = stats.click_count || 0;
    const responseCount = stats.response_count || 0;
    const bounceCount = stats.bounce_count || 0;
    const unsubscribeCount = stats.unsubscribe_count || 0;
    
    // Calculate rates
    const deliveryRate = totalRecipients > 0 ? (deliveredCount / totalRecipients) * 100 : 0;
    const openRate = deliveredCount > 0 ? (openCount / deliveredCount) * 100 : 0;
    const clickRate = deliveredCount > 0 ? (clickCount / deliveredCount) * 100 : 0;
    const responseRate = deliveredCount > 0 ? (responseCount / deliveredCount) * 100 : 0;
    const bounceRate = totalRecipients > 0 ? (bounceCount / totalRecipients) * 100 : 0;
    const unsubscribeRate = deliveredCount > 0 ? (unsubscribeCount / deliveredCount) * 100 : 0;
    
    // Format period (YYYY-MM)
    const date = new Date(campaign.created_at);
    const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    
    return {
      id: campaign.id,
      name: campaign.name,
      totalRecipients,
      deliveredCount,
      deliveryRate,
      openCount,
      openRate,
      clickCount,
      clickRate,
      responseCount,
      responseRate,
      bounceCount,
      bounceRate,
      unsubscribeCount,
      unsubscribeRate,
      period,
      templateId: campaign.template_id,
      templateName: campaign.templates?.name || 'Unknown'
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get recipient engagement for a campaign
 */
export const getCampaignRecipientEngagement = async (
  campaignId: string
): Promise<{
  totalRecipients: number;
  statusBreakdown: Record<string, number>;
  topEngagedContacts: ContactEngagement[];
  organizationBreakdown: { name: string; count: number; openRate: number }[];
}> => {
  try {
    // If campaignId is 'all', we'll analyze engagement across all campaigns
    const isAllCampaigns = campaignId === 'all';
    
    // Get campaign information first
    let campaignQuery = supabase
      .from('email_campaigns')
      .select('id, name');
      
    if (!isAllCampaigns) {
      campaignQuery = campaignQuery.eq('id', campaignId);
    }
    
    const { data: campaigns, error: campaignError } = await campaignQuery;
    
    if (campaignError) {
      throw campaignError;
    }
    
    // Get recipient data, joining with contacts and organizations
    let recipientQuery = supabase
      .from('email_campaign_recipients')
      .select(`
        id,
        campaign_id,
        contact_id,
        status,
        open_count,
        click_count,
        responded,
        last_activity_date,
        contacts (
          id,
          first_name,
          last_name,
          organization_id,
          organizations (
            id,
            name
          )
        )
      `);
      
    if (!isAllCampaigns) {
      recipientQuery = recipientQuery.eq('campaign_id', campaignId);
    }
    
    const { data: recipients, error: recipientError } = await recipientQuery;
    
    if (recipientError) {
      throw recipientError;
    }
    
    // Calculate status breakdown
    const statusBreakdown: Record<string, number> = {
      delivered: 0,
      opened: 0,
      clicked: 0,
      replied: 0,
      bounced: 0,
      unsubscribed: 0
    };
    
    recipients?.forEach(recipient => {
      // Count statuses
      const status = recipient.status?.toLowerCase() || 'unknown';
      
      if (status === 'delivered') {
        statusBreakdown.delivered = (statusBreakdown.delivered || 0) + 1;
      } else if (status === 'opened' || recipient.open_count > 0) {
        statusBreakdown.opened = (statusBreakdown.opened || 0) + 1;
      } else if (status === 'clicked' || recipient.click_count > 0) {
        statusBreakdown.clicked = (statusBreakdown.clicked || 0) + 1;
      } else if (status === 'responded' || recipient.responded) {
        statusBreakdown.replied = (statusBreakdown.replied || 0) + 1;
      } else if (status === 'bounced') {
        statusBreakdown.bounced = (statusBreakdown.bounced || 0) + 1;
      } else if (status === 'unsubscribed') {
        statusBreakdown.unsubscribed = (statusBreakdown.unsubscribed || 0) + 1;
      }
    });
    
    // Calculate organization breakdown
    const organizationMap = new Map<string, {
      name: string,
      count: number,
      opens: number,
      total: number
    }>();
    
    recipients?.forEach(recipient => {
      const organization = recipient.contacts?.organizations;
      if (!organization) return;
      
      const orgId = organization.id;
      const orgName = organization.name;
      
      const current = organizationMap.get(orgId) || {
        name: orgName,
        count: 0,
        opens: 0,
        total: 0
      };
      
      current.count += 1;
      current.total += 1;
      
      if (recipient.open_count > 0 || recipient.status === 'opened') {
        current.opens += 1;
      }
      
      organizationMap.set(orgId, current);
    });
    
    const organizationBreakdown = Array.from(organizationMap.values())
      .map(org => ({
        name: org.name,
        count: org.count,
        openRate: org.total > 0 ? (org.opens / org.total) * 100 : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5); // Top 5 organizations
    
    // Aggregate contact engagement data
    const contactMap = new Map<string, {
      contactId: string,
      contactName: string,
      organizationId?: string,
      organizationName?: string,
      campaigns: Set<string>,
      opens: number,
      clicks: number,
      responses: number,
      total: number,
      lastEngagement?: string,
      recentActivities: {
        campaignId: string,
        campaignName: string,
        date: string,
        action: string
      }[]
    }>();
    
    recipients?.forEach(recipient => {
      const contact = recipient.contacts;
      if (!contact) return;
      
      const contactId = contact.id;
      const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
      const organizationId = contact.organization_id;
      const organizationName = contact.organizations?.name;
      
      // Get or create contact data
      const contactData = contactMap.get(contactId) || {
        contactId,
        contactName,
        organizationId,
        organizationName,
        campaigns: new Set<string>(),
        opens: 0,
        clicks: 0,
        responses: 0,
        total: 0,
        recentActivities: []
      };
      
      // Add this campaign to the set
      contactData.campaigns.add(recipient.campaign_id);
      contactData.total += 1;
      
      // Count engagement
      if (recipient.open_count > 0 || recipient.status === 'opened') {
        contactData.opens += 1;
      }
      
      if (recipient.click_count > 0 || recipient.status === 'clicked') {
        contactData.clicks += 1;
      }
      
      if (recipient.responded || recipient.status === 'responded') {
        contactData.responses += 1;
      }
      
      // Track most recent engagement
      if (recipient.last_activity_date) {
        if (!contactData.lastEngagement || 
            new Date(recipient.last_activity_date) > new Date(contactData.lastEngagement)) {
          contactData.lastEngagement = recipient.last_activity_date;
        }
        
        // Determine action type
        let action = 'delivered';
        if (recipient.responded || recipient.status === 'responded') {
          action = 'replied';
        } else if (recipient.click_count > 0 || recipient.status === 'clicked') {
          action = 'clicked';  
        } else if (recipient.open_count > 0 || recipient.status === 'opened') {
          action = 'opened';
        }
        
        // Add to recent activities
        const campaign = campaigns?.find(c => c.id === recipient.campaign_id);
        if (campaign) {
          contactData.recentActivities.push({
            campaignId: campaign.id,
            campaignName: campaign.name,
            date: recipient.last_activity_date,
            action
          });
        }
      }
      
      // Sort activities by date (newest first) and limit to 5
      contactData.recentActivities.sort((a, b) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      
      contactData.recentActivities = contactData.recentActivities.slice(0, 5);
      
      // Update contact data in map
      contactMap.set(contactId, contactData);
    });
    
    // Convert to array and calculate rates
    const contactEngagement: ContactEngagement[] = Array.from(contactMap.values())
      .map(contact => ({
        contactId: contact.contactId,
        contactName: contact.contactName,
        organizationId: contact.organizationId,
        organizationName: contact.organizationName,
        totalCampaigns: contact.campaigns.size,
        openRate: contact.total > 0 ? (contact.opens / contact.total) * 100 : 0,
        clickRate: contact.total > 0 ? (contact.clicks / contact.total) * 100 : 0,
        responseRate: contact.total > 0 ? (contact.responses / contact.total) * 100 : 0,
        lastEngagement: contact.lastEngagement,
        recentActivity: contact.recentActivities
      }))
      // Sort by engagement score (weighted combination of open, click, and response rates)
      .sort((a, b) => {
        const scoreA = a.openRate * 0.3 + a.clickRate * 0.3 + a.responseRate * 0.4;
        const scoreB = b.openRate * 0.3 + b.clickRate * 0.3 + b.responseRate * 0.4;
        return scoreB - scoreA;
      })
      .slice(0, 10); // Top 10 engaged contacts
    
    return {
      totalRecipients: recipients?.length || 0,
      statusBreakdown,
      topEngagedContacts: contactEngagement,
      organizationBreakdown
    };
  } catch (error) {
    // Return empty data structure on error
    return {
      totalRecipients: 0,
      statusBreakdown: {
        delivered: 0,
        opened: 0,
        clicked: 0,
        replied: 0,
        bounced: 0,
        unsubscribed: 0
      },
      topEngagedContacts: [],
      organizationBreakdown: []
    };
  }
};

/**
 * Get contact engagement across campaigns
 */
export const getContactEngagement = async (
  contactId: string
): Promise<ContactEngagement> => {
  try {
    // First get the contact details
    const { data: contact, error: contactError } = await supabase
      .from('contacts')
      .select(`
        id,
        first_name,
        last_name,
        organization_id,
        organizations (
          id,
          name
        )
      `)
      .eq('id', contactId)
      .single();
    
    if (contactError) {
      throw contactError;
    }
    
    if (!contact) {
      throw new Error(`Contact with ID ${contactId} not found`);
    }
    
    // Get all campaign recipients for this contact
    const { data: recipients, error: recipientError } = await supabase
      .from('email_campaign_recipients')
      .select(`
        id,
        campaign_id,
        status,
        open_count,
        click_count,
        responded,
        last_activity_date,
        email_campaigns (
          id, 
          name
        )
      `)
      .eq('contact_id', contactId);
    
    if (recipientError) {
      throw recipientError;
    }
    
    // Calculate engagement metrics
    const totalCampaigns = recipients?.length || 0;
    let openCount = 0;
    let clickCount = 0;
    let responseCount = 0;
    let lastEngagementDate: string | undefined;
    
    // Process each campaign interaction
    const recentActivity = recipients?.map(recipient => {
      // Count engagement
      if (recipient.open_count > 0 || recipient.status === 'opened') {
        openCount++;
      }
      
      if (recipient.click_count > 0 || recipient.status === 'clicked') {
        clickCount++;
      }
      
      if (recipient.responded || recipient.status === 'responded') {
        responseCount++;
      }
      
      // Track last engagement
      if (recipient.last_activity_date) {
        if (!lastEngagementDate || 
            new Date(recipient.last_activity_date) > new Date(lastEngagementDate)) {
          lastEngagementDate = recipient.last_activity_date;
        }
      }
      
      // Determine action type
      let action = 'delivered';
      if (recipient.responded || recipient.status === 'responded') {
        action = 'replied';
      } else if (recipient.click_count > 0 || recipient.status === 'clicked') {
        action = 'clicked';  
      } else if (recipient.open_count > 0 || recipient.status === 'opened') {
        action = 'opened';
      }
      
      // Return activity data
      return {
        campaignId: recipient.campaign_id,
        campaignName: recipient.email_campaigns?.name || 'Unknown Campaign',
        date: recipient.last_activity_date || new Date().toISOString(),
        action
      };
    }) || [];
    
    // Sort by date (newest first) and limit to 5
    recentActivity.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    // Calculate rates
    const openRate = totalCampaigns > 0 ? (openCount / totalCampaigns) * 100 : 0;
    const clickRate = totalCampaigns > 0 ? (clickCount / totalCampaigns) * 100 : 0;
    const responseRate = totalCampaigns > 0 ? (responseCount / totalCampaigns) * 100 : 0;
    
    return {
      contactId,
      contactName: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
      organizationId: contact.organization_id,
      organizationName: contact.organizations?.name,
      totalCampaigns,
      openRate,
      clickRate,
      responseRate,
      lastEngagement: lastEngagementDate,
      recentActivity: recentActivity.slice(0, 5)
    };
  } catch (error) {
    // Return default structure with empty data
    return {
      contactId,
      contactName: "Unknown Contact",
      organizationId: undefined,
      organizationName: undefined,
      totalCampaigns: 0,
      openRate: 0,
      clickRate: 0,
      responseRate: 0,
      recentActivity: []
    };
  }
};

/**
 * Get analytics comparing campaigns
 */
export const compareCampaigns = async (
  campaignIds: string[]
): Promise<{
  campaigns: CampaignMetrics[];
  comparison: {
    field: string;
    label: string;
    values: { id: string; name: string; value: number }[];
  }[];
}> => {
  try {
    // Fetch metrics for each campaign individually to ensure we have the most current data
    const campaignsPromises = campaignIds.map(id => getCampaignMetrics(id));
    const campaignsResults = await Promise.allSettled(campaignsPromises);
    
    // Filter out rejected promises and extract values from fulfilled ones
    const selectedCampaigns: CampaignMetrics[] = campaignsResults
      .filter((result): result is PromiseFulfilledResult<CampaignMetrics> => 
        result.status === 'fulfilled'
      )
      .map(result => result.value);
    
    if (selectedCampaigns.length === 0) {
      throw new Error("No valid campaigns found for comparison");
    }
    
    // Define comparison fields
    const comparisonFields = [
      { field: "openRate", label: "Open Rate" },
      { field: "clickRate", label: "Click Rate" },
      { field: "responseRate", label: "Response Rate" },
      { field: "bounceRate", label: "Bounce Rate" },
      { field: "unsubscribeRate", label: "Unsubscribe Rate" }
    ];
    
    // Create comparison data structure
    const comparison = comparisonFields.map(({ field, label }) => ({
      field,
      label,
      values: selectedCampaigns.map(c => ({
        id: c.id,
        name: c.name,
        value: c[field as keyof CampaignMetrics] as number
      }))
    }));
    
    return {
      campaigns: selectedCampaigns,
      comparison
    };
  } catch (error) {
    // Return empty data structure on error
    return {
      campaigns: [],
      comparison: [
        { field: "openRate", label: "Open Rate", values: [] },
        { field: "clickRate", label: "Click Rate", values: [] },
        { field: "responseRate", label: "Response Rate", values: [] },
        { field: "bounceRate", label: "Bounce Rate", values: [] },
        { field: "unsubscribeRate", label: "Unsubscribe Rate", values: [] }
      ]
    };
  }
};